import React, { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Heart, Leaf, Users, ArrowRight, CheckCircle, Star, Phone, Mail, Globe, Baby, Flower2, BookOpen, Coffee, Home, Sparkles, Shield, MessageCircle, Brain, Target, Bed, Apple } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { BOOKING_URL } from '@/constants/urls';
import postpartumImage from '@/assets/postpartum.jpg';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';

const PostpartumPage = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Force scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add dynamic meta tags and structured data
  useEffect(() => {
    // Update document title and meta tags
    document.title = "Postpartum Care Sunshine Coast | Chinese Medicine & Acupuncture";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Gentle, personalised postpartum care in Currimundi. Restore energy, hormones & emotional balance with acupuncture, herbs & in-home support.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Postpartum Care Sunshine Coast | Chinese Medicine & Acupuncture");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Gentle, personalised postpartum care in Currimundi. Restore energy, hormones & emotional balance with acupuncture, herbs & in-home support.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/postpartum");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/postpartum.jpg");
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Postpartum Care Sunshine Coast | Chinese Medicine & Acupuncture");
    }

    // Add LocalBusiness Schema for postpartum services
    const postpartumSchema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Postpartum Care",
      "image": "https://womenandtheway.com.au/postpartum.jpg",
      "description": "Gentle, personalised postpartum care in Currimundi. Restore energy, hormones & emotional balance with acupuncture, herbs & in-home support.",
      "url": "https://womenandtheway.com.au/postpartum",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Postpartum Care, Acupuncture, Chinese Medicine",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(postpartumSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
      <NavBar />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 md:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Chinese Medicine for Postpartum
              </h1>
              <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                Restoring your vitality, one breath at a time.
              </p>
              <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                The postpartum period is a sacred time of healing, bonding, and transformation. At Women and the Way, we honor this unique season with gentle, nurturing care that supports your physical recovery, emotional wellbeing, and the beautiful transition into motherhood.
              </p>
              <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                Whether you're a few days postpartum or navigating the challenges of the fourth trimester, our treatments help restore your energy, balance your hormones, and support your emotional health. Our approach combines the wisdom of Traditional Chinese Medicine with modern understanding of postpartum physiology. Many of our clients also benefit from our <a href="/emotional-healing" className="text-sage-600 hover:text-sage-800 underline transition-colors">emotional healing services</a> and <a href="/sleep-support" className="text-sage-600 hover:text-sage-800 underline transition-colors">sleep support</a> during this challenging time.
              </p>
              <Button 
                size="lg" 
                className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                onClick={handleBooking}
                disabled={isLoading}
              >
                {isLoading ? 'Booking...' : 'Book Now'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            <div className="relative">
              <img 
                src={postpartumImage} 
                alt="Woman receiving gentle acupuncture for postpartum healing" 
                className="rounded-2xl shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Postpartum Care Matters */}
      <section className="py-16 px-4 md:px-6 bg-sage-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 flex items-center justify-center gap-3 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              <Flower2 className="w-8 h-8 text-sage-600" />
              Why Postpartum Care Matters
            </h2>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
            <p className="text-lg text-sage-700 mb-6 leading-relaxed">
              In Chinese medicine, the postpartum window is considered a "Golden Month"  -  a critical time to rebuild the blood, replenish Qi (vital energy), and protect long-term health. Without proper care, postnatal depletion can affect everything from your hormones to your emotional wellbeing and future fertility.
            </p>
            <p className="text-lg text-sage-700 leading-relaxed">
              Our approach recognises that healing doesn't stop at six weeks  -  in fact, that's often when the real work begins.
            </p>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 px-4 md:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              What We Offer
            </h2>
            <p className="text-xl text-sage-600 mb-8">
              In-Clinic, At-Home, or Online Postpartum Support
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Home className="w-6 h-6 text-sage-600" />
                  <h3 className="font-serif text-2xl font-semibold text-sage-800">In-Clinic Care</h3>
                </div>
                <p className="text-sage-700 mb-4">
                  Visit our peaceful Currimundi clinic for comprehensive postpartum support in a nurturing environment designed for healing and restoration.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-lg">🪡</span>
                    <p className="text-sage-700">Gentle acupuncture to regulate hormones, calm the nervous system, and promote deep rest</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-lg">🌿</span>
                    <p className="text-sage-700">Chinese herbal medicine tailored to your specific needs and recovery stage</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-lg">🔥</span>
                    <p className="text-sage-700">Mother warming (moxibustion) to revitalise the womb space and restore vital energy</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-lg">💆‍♀️</span>
                    <p className="text-sage-700">Supportive bodywork and gentle massage techniques for tension release</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Heart className="w-6 h-6 text-sage-600" />
                  <h3 className="font-serif text-2xl font-semibold text-sage-800">At-Home Care</h3>
                </div>
                <p className="text-sage-700 mb-4 font-medium">
                  Home Postpartum Visits
                </p>
                <p className="text-sage-700 mb-4">
                  Let the healing come to you. Our in-home postpartum care sessions are tailored to your specific needs and may include:
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-lg">🪡</span>
                    <p className="text-sage-700">Gentle acupuncture, moxa, and warming herbal compresses</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-lg">💝</span>
                    <p className="text-sage-700">Hands-on emotional support and cycle education</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-lg">🏠</span>
                    <p className="text-sage-700">Tailored assistance with daily rhythms  -  from organising nourishing meals to helping with light washing or creating a healing sanctuary space</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-lg">🌱</span>
                    <p className="text-sage-700">Guidance for creating sustainable self-care practices in your new routine</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="w-6 h-6 text-sage-600" />
                  <h3 className="font-serif text-2xl font-semibold text-sage-800">Online Care</h3>
                </div>
                <p className="text-sage-700 mb-4 font-medium">
                  We See You, Mumma
                </p>
                <p className="text-sage-700 mb-4">
                  Sometimes getting to the clinic feels impossible. And maybe you're not quite ready for someone in your home just yet. That's okay  -  you still deserve care.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-lg">💬</span>
                    <p className="text-sage-700">Phone consults to talk through your concerns, symptoms, or emotional challenges</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-lg">🌿</span>
                    <p className="text-sage-700">Custom Chinese herbal prescriptions mailed to your door or available for pick-up at our clinic</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-lg">🤍</span>
                    <p className="text-sage-700">Supportive conversations that meet you where you are  -  with kindness, practical advice, and space to be heard</p>
                  </div>
                </div>
                <p className="text-sage-700 mt-4 font-medium">
                  No pressure. No perfection. Just care that fits your life right now.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-sage-50 rounded-xl p-6 text-center">
            <p className="text-lg text-sage-700 mb-4">
              <strong>We recommend 4–6 sessions after birth, and 8–12 sessions between 6–12 months postpartum</strong> to support your ongoing hormonal recalibration.
            </p>
          </div>
        </div>
      </section>

      {/* Postpartum Timeline */}
      <section className="py-16 px-4 md:px-6 bg-sage-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              The Postpartum Timeline
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
              <AccordionTrigger className="px-6 py-4 text-left">
                <div className="flex items-center gap-3">
                  <Baby className="w-6 h-6 text-sage-600" />
                  <span className="font-serif text-xl font-semibold text-sage-800">0–5 Months Postpartum: Cocoon & Rebuild</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-4">
                  <p className="text-sage-700 font-medium">✨ The focus here is restoration, rest, and warmth.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sage-800">What we support:</h4>
                      <ul className="space-y-1 text-sage-700">
                        <li>• Rebuild blood & Qi</li>
                        <li>• Support lactation & breast health</li>
                        <li>• Aid tissue healing (especially after C-section or tearing)</li>
                        <li>• Calm emotional overwhelm, night sweats, insomnia</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sage-800">Common areas of support:</h4>
                      <ul className="space-y-1 text-sage-700">
                        <li>• Support for prolapse, fatigue, or poor milk supply</li>
                        <li>• Clear lochia and stagnation with appropriate herbs</li>
                      </ul>
                    </div>
                  </div>
                  <blockquote className="border-l-4 border-sage-300 pl-4 italic text-sage-700">
                    "Giving to the mother means giving to the baby. Nourish her, and everyone thrives."
                    <br />
                    <span className="text-sm"> -  The Golden Month, Heng Ou</span>
                  </blockquote>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg">
              <AccordionTrigger className="px-6 py-4 text-left">
                <div className="flex items-center gap-3">
                  <Flower2 className="w-6 h-6 text-sage-600" />
                  <span className="font-serif text-xl font-semibold text-sage-800">6–12 Months Postpartum: Recalibrate & Reconnect</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-4">
                  <p className="text-sage-700">
                    This stage often brings hormonal shifts as ovulation resumes and depletion catches up. You might notice:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sage-800">Common symptoms:</h4>
                      <ul className="space-y-1 text-sage-700">
                        <li>• Energy dips or inconsistent moods</li>
                        <li>• Painful, heavy, or irregular periods</li>
                        <li>• Anxiety, brain fog, or low libido</li>
                        <li>• Sleep struggles and digestive changes</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sage-800">Our care focuses on:</h4>
                      <ul className="space-y-1 text-sage-700">
                        <li>• Regulating your menstrual cycle</li>
                        <li>• Balancing hormones gently</li>
                        <li>• Supporting your nervous system & sleep</li>
                        <li>• Seed cycling and castor oil packs</li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-sage-700 font-medium">✨ You are not broken  -  you're in a long, sacred unfolding.</p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Conditions We Support */}
      <section className="py-16 px-4 md:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Conditions We Commonly Support
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { western: "Mastitis", chinese: "Heat & stagnation in Liver/Stomach channels" },
              { western: "Fatigue", chinese: "Qi & Blood deficiency" },
              { western: "Haemorrhoids", chinese: "Spleen Qi sinking or Blood heat" },
              { western: "Poor milk supply", chinese: "Qi/Blood deficiency or Liver Qi stagnation" },
              { western: "Emotional overwhelm", chinese: "Heart/Spleen imbalance" },
              { western: "C-section recovery", chinese: "Blood stasis, Qi deficiency" },
              { western: "Irregular periods", chinese: "Liver Qi stagnation, Blood deficiency" },
              { western: "Incontinence/prolapse", chinese: "Kidney/Spleen Qi deficiency" },
              { western: "Night sweats", chinese: "Yin deficiency" },
              { western: "Digestive issues", chinese: "Spleen Qi or Yang deficiency" }
            ].map((condition, index) => (
              <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-sage-800 mb-2">{condition.western}</h3>
                  <p className="text-sage-600 text-sm italic">{condition.chinese}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-16 px-4 md:px-6 bg-sage-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              What Makes Our Approach Different
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: "🫶", title: "Personalised care", desc: "Every formula, care plan, and approach is unique to your body and your season" },
              { icon: "🌿", title: "Natural and effective", desc: "Rooted in centuries of wisdom, grounded in real-world results" },
              { icon: "📚", title: "Education-focused", desc: "We share tools and knowledge you can use daily" },
              { icon: "👩‍👧‍👦", title: "Mother-centred", desc: "Because when the mother is well, the whole family thrives" }
            ].map((feature, index) => (
              <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl mb-3">{feature.icon}</div>
                  <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">{feature.title}</h3>
                  <p className="text-sage-700">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Support Tools */}
      <section className="py-16 px-4 md:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Support Tools We Offer in Clinic
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Coffee, title: "Food of Origin Broths", desc: "Warming, herbal blends for digestion and recovery" },
              { icon: Leaf, title: "Postpartum teas", desc: "Seed cycling blends and herbal formulas" },
              { icon: Heart, title: "Castor oil packs", desc: "With how-to instructions for home use" },
              { icon: BookOpen, title: "Chinese herbal formulas", desc: "In capsule or tea form" },
              { icon: BookOpen, title: "Recommended reading", desc: "The First Forty Days, The Postnatal Depletion Cure, The Complete Guide to Postpartum, The Fifth Vital Sign" }
            ].map((tool, index) => (
              <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <tool.icon className="w-8 h-8 text-sage-600 mx-auto mb-3" />
                  <h3 className="font-serif text-lg font-semibold text-sage-800 mb-2">{tool.title}</h3>
                  <p className="text-sage-700 text-sm">{tool.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 md:px-6 bg-sage-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
            Book Your Postpartum Session
          </h2>
          <p className="text-xl text-sage-700 mb-8">
            Whether you're 6 weeks, 6 months, or somewhere in between  -  you deserve to feel supported, seen, and replenished.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-center justify-center gap-3 text-sage-700">
              <MapPin className="w-5 h-5 text-sage-600" />
              <a 
                href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-sage-800 transition-colors"
              >
                700 Nicklin Way, Currimundi
              </a>
            </div>
            <div className="flex items-center justify-center gap-3 text-sage-700">
              <Phone className="w-5 h-5 text-sage-600" />
              <a 
                href="tel:0415537784" 
                className="hover:text-sage-800 transition-colors"
              >
                0415 537 784
              </a>
            </div>
            <div className="flex items-center justify-center gap-3 text-sage-700">
              <Mail className="w-5 h-5 text-sage-600" />
              <a 
                href="mailto:womenandtheway@gmail.com" 
                className="hover:text-sage-800 transition-colors"
              >
                womenandtheway@gmail.com
              </a>
            </div>
          </div>
          
          <Button 
            size="lg" 
            className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
            onClick={handleBooking}
            disabled={isLoading}
          >
            <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : '🌱 Start where you are. We\'ll walk with you from there.'}</span>
            <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
          </Button>
        </div>
      </section>

      <ScrollDownArrow />
      <ReturnToTop />
    </div>
  );
};

export default PostpartumPage;
